import React, { useEffect, useRef, useState } from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { Cart } from "custom-types/Cart";
import { MenuItem } from "custom-types/MenuItem";
import { MenuItemV2 } from "custom-types/MenuItemV2";
import { VariantWithDeliveryInfo } from "custom-types/Variant";
import { useATBStats } from "hooks/useATBStats";
import { useEventTracker } from "hooks/useEventTracker";
import {
  LoadCartAction,
  RESERVATION_LOAD_CART,
} from "redux/reducers/reservation";
import { getCartId } from "redux/selectors/reservation";
import { isInValidationGroup } from "utils/dispensaryUtils";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category, Label } from "utils/trackEvent";

import { AddToBagModal } from "components/AddToBagModal";
import Button from "components/botanic/Button";
import { useIsJaneCheckoutEnabled } from "components/Checkout/JaneCheckout";

type AddToBagButtonProps = {
  buttonSize?: string;
  defaultVariantId: number;
  immediatelyAddToBag?: boolean;
  menuItem: MenuItem | MenuItemV2 | VariantWithDeliveryInfo;
  quantity?: number;
  onClick?: () => void;
  onReservation?: () => void;
  splitClickEventName?: string;
  splitPageProperty?: string;
  ctaText?: string;
  addingToBagText?: string;
  successText?: string;
  variant?: string;
  iconDimensions?: string;
  merchandisingCampaignId?: number;
};

export const AddToBagButton: React.FC<AddToBagButtonProps> = ({
  buttonSize = "small",
  ctaText = "add to cart",
  addingToBagText = "adding",
  successText = "added!",
  variant,
  defaultVariantId,
  immediatelyAddToBag = false,
  menuItem,
  quantity,
  onClick = () => {},
  onReservation = () => {},
  splitClickEventName,
  splitPageProperty,
  merchandisingCampaignId,
  iconDimensions,
}) => {
  const cartId = useSelector(getCartId) as string; // You will have a cart id by the time you open the modal
  const dispatch = useDispatch();
  const [viewingModal, setViewingModal] = useState(false);
  const buttonRef = useRef(null);
  const { reportATBStats } = useATBStats();
  const isJaneCheckoutEnabled = useIsJaneCheckoutEnabled();
  const { publishEvent } = useEventTracker();

  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  useEffect(() => {
    if (showSuccessMessage) {
      setIsAddingToBag(false);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 1200);
    }
  }, [showSuccessMessage]);

  const { treatments, isLoading } = useClientSideSplitTreatments(
    "shopEveryMenuMVP_test",
    getSplitKey()
  );
  const shopEveryMenuMVPEnabled =
    !isLoading && treatments.shopEveryMenuMVP_test.treatment === "on";

  const addToBag = () => {
    setIsAddingToBag(true);
    onClick();
    publishEvent({
      action: Action.click,
      cartId,
      category: Category.addToBag,
      janeCheckoutEnabled: isJaneCheckoutEnabled,
      label:
        shopEveryMenuMVPEnabled && isInValidationGroup(menuItem?.dispensary?.id)
          ? "add to bag - shop every menu"
          : Label.addToBag,
      menuItemId: menuItem.id,
      merchandisingCampaignId: merchandisingCampaignId,
      productId: defaultVariantId,
    });
    setViewingModal(true);
  };

  // Once the AddToBagModal renders, it handles adding the menu item to bag in a useEffect
  return (
    <>
      <Button
        data-testid="add-to-cart-button"
        iconFilePath="cart_alt.svg"
        disabled={showSuccessMessage}
        onClick={addToBag}
        size={buttonSize}
        iconDimensions={iconDimensions}
        className={classNames("w-full disabled", {
          "py-sm": buttonSize === "small",
        })}
        ref={buttonRef}
        variant={variant}
        loadingText={addingToBagText}
        isLoading={isAddingToBag}
      >
        {showSuccessMessage ? successText : ctaText}
      </Button>

      {viewingModal && (
        <AddToBagModal
          quantity={quantity}
          onCartFetch={(cart: Cart) => {
            dispatch<LoadCartAction>({
              cart,
              type: RESERVATION_LOAD_CART,
            });

            if (typeof document !== "undefined") {
              // Signal to cephalopod to refresh the cart
              document.dispatchEvent(new CustomEvent("getcart"));
            }
          }}
          onDismiss={() => {
            setViewingModal(false);
            setIsAddingToBag(false);
          }}
          onReservation={() => {
            reportATBStats({ atbAction: true, atbError: false });
            setShowSuccessMessage(true);
            onReservation();
          }}
          onReservationFail={() => {
            setIsAddingToBag(false);
            reportATBStats({ atbAction: true, atbError: true });
          }}
          returnFocusRef={buttonRef}
          splitEventName={splitClickEventName}
          splitPageProperty={splitPageProperty}
          useAddedToBagPopout={true}
          immediatelyAddToBag={immediatelyAddToBag}
          variantId={defaultVariantId}
        />
      )}
    </>
  );
};
